<template>
  <div class="theme-area-chart">
    <LineChart
      :chart-data="chartData"
      :chart-options="chartOptions"
    />
  </div>
</template>

<script>
import { Line } from 'vue-chartjs/legacy';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

export default {
  name: 'ThemeAreaChart',
  components: { LineChart: Line },
  
  props: {
    categories: {
      type: Array,
      required: true
    },
    data: {
      type: Array,
      required: true
    },
    label: {
      type: String,
      default: 'Data'
    },
    yAxisLabel: {
      type: String,
      default: ''
    },
    formatValue: {
      type: Function,
      default: (value) => value.toLocaleString()
    }
  },

  data() {
    return {
      isDarkMode: document.documentElement.classList.contains('dark'),
      themeObserver: null
    };
  },

  mounted() {
    // Watch for theme changes
    this.themeObserver = new MutationObserver(() => {
      this.isDarkMode = document.documentElement.classList.contains('dark');
    });
    
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
  },

  beforeDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },

  computed: {
    chartData() {
      if (!this.data || !Array.isArray(this.data) || !this.categories || !Array.isArray(this.categories)) {
        return {
          labels: [],
          datasets: []
        };
      }

      const color = '#3b82f6';
      
      return {
        labels: this.categories,
        datasets: [{
          label: this.label,
          data: this.data.map(v => parseFloat(v)),
          borderColor: color,
          backgroundColor: this.isDarkMode 
            ? 'rgba(59, 130, 246, 0.2)' 
            : 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: color,
          pointBorderColor: '#fff',
          pointBorderWidth: 2
        }]
      };
    },

    chartOptions() {
      const textColor = this.isDarkMode ? '#94a3b8' : '#374151';
      const gridColor = this.isDarkMode ? '#1e293b' : '#e5e7eb';
      const tooltipBg = this.isDarkMode ? '#1e293b' : '#ffffff';
      const tooltipBorder = this.isDarkMode ? '#334155' : '#e5e7eb';

      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: tooltipBg,
            titleColor: textColor,
            bodyColor: textColor,
            borderColor: tooltipBorder,
            borderWidth: 1,
            padding: 12,
            callbacks: {
              label: (context) => {
                const label = context.dataset.label || '';
                const value = context.parsed.y;
                return `${label}: ${this.formatValue(value)}`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: {
              color: gridColor,
              drawBorder: false
            },
            ticks: {
              color: textColor
            }
          },
          y: {
            grid: {
              color: gridColor,
              drawBorder: false
            },
            ticks: {
              color: textColor,
              callback: (value) => this.formatValue(value)
            },
            title: {
              display: !!this.yAxisLabel,
              text: this.yAxisLabel,
              color: textColor
            }
          }
        }
      };
    }
  }
};
</script>
